import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Card, Popover, Tooltip } from 'antd';
import {
  AudioOutlined,
  ArrowLeftOutlined,
  DesktopOutlined,
  EnvironmentOutlined,
  FileOutlined,
  PaperClipOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import UploadDrawer from './UploadDrawer';
import LocationSendDrawer from './ConversationComps/LocationSendDrawer';
import { MEDIA_KINDS } from './mediaKindUtils';

const TYPE_ITEMS = [
  { key: 'image', label: 'Image', icon: PictureOutlined, color: '#5b72e8' },
  { key: 'video', label: 'Video', icon: VideoCameraOutlined, color: '#e75a7c' },
  { key: 'audio', label: 'Audio', icon: AudioOutlined, color: '#f5a623' },
  { key: 'document', label: 'Document', icon: FileOutlined, color: '#7e57c2' },
  { key: 'location', label: 'Location', icon: EnvironmentOutlined, color: '#25d366' },
];

const cardStyle = {
  width: 88,
  borderRadius: 12,
  border: 'none',
  background: '#f5f6f6',
  cursor: 'pointer',
  textAlign: 'center',
};

const cardBodyStyle = {
  padding: '12px 8px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
};

const sourceRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 12px',
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'background 0.15s',
};

const AttachmentMenu = ({
  uploadDrawerProps,
  onSendLocation,
  variant = 'paperclip',
  showLabel,
  disabled,
  isMobile = false,
  onMenuSelect,
  ...uploadDrawerRest
}) => {
  // ChatContentFooter spreads upload props flat; support both patterns.
  const drawerProps = uploadDrawerProps ?? {
    ...uploadDrawerRest,
    ...(disabled !== undefined ? { disabled } : {}),
  };
  const isDisabled = disabled ?? drawerProps.disabled;
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [view, setView] = useState('grid');
  const [activeKind, setActiveKind] = useState(null);
  const [openLibraryKind, setOpenLibraryKind] = useState(null);
  const [locationOpen, setLocationOpen] = useState(false);
  const [devicePickerKind, setDevicePickerKind] = useState(null);
  const uploadRef = useRef(null);
  const deviceInputRef = useRef(null);
  const pendingDeviceKindRef = useRef(null);

  const handleLibraryOpened = useCallback(() => {
    setOpenLibraryKind(null);
  }, []);

  const resetPopover = () => {
    setView('grid');
    setActiveKind(null);
  };

  const closePopover = () => {
    setPopoverOpen(false);
    resetPopover();
  };

  const handleKindSelect = (key) => {
    if (key === 'location') {
      closePopover();
      setLocationOpen(true);
      onMenuSelect?.();
      return;
    }

    pendingDeviceKindRef.current = key;
    setDevicePickerKind(key);
    setActiveKind(key);
    setView('source');
  };

  const handleLibrarySelect = () => {
    const kind = activeKind;
    if (!kind) {
      return;
    }

    closePopover();
    onMenuSelect?.();
    setOpenLibraryKind(kind);
  };

  const handleDeviceFileChange = async (event) => {
    const kind = pendingDeviceKindRef.current || devicePickerKind || activeKind;
    const files = Array.from(event.target.files || []);
    event.target.value = '';

    if (!kind || !files.length) {
      pendingDeviceKindRef.current = null;
      setDevicePickerKind(null);
      return;
    }

    closePopover();
    onMenuSelect?.();
    await uploadRef.current?.processDeviceFiles(files, kind);
    pendingDeviceKindRef.current = null;
    setDevicePickerKind(null);
  };

  const kindLabel = MEDIA_KINDS[activeKind]?.label || 'Media';
  const deviceAccept = devicePickerKind ? MEDIA_KINDS[devicePickerKind]?.accept : undefined;
  const canPickFromDevice = Boolean(activeKind && !isDisabled);

  const openDevicePicker = useCallback(() => {
    if (!activeKind || isDisabled || !deviceInputRef.current) {
      return;
    }
    deviceInputRef.current.click();
  }, [activeKind, isDisabled]);

  const gridContent = (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 88px)',
        gap: 8,
        padding: 4,
      }}
    >
      {TYPE_ITEMS.map((item) => {
        const Icon = item.icon;
        return (
          <Card
            key={item.key}
            hoverable
            style={cardStyle}
            bodyStyle={cardBodyStyle}
            onClick={() => handleKindSelect(item.key)}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: item.color,
                fontSize: 20,
              }}
            >
              <Icon />
            </div>
            <span style={{ fontSize: 12, color: '#3b4a54', fontWeight: 500 }}>
              {item.label}
            </span>
          </Card>
        );
      })}
    </div>
  );

  const sourceContent = (
    <div style={{ width: 220, padding: '4px 0' }}>
      <button
        type="button"
        onClick={resetPopover}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          border: 'none',
          background: 'transparent',
          color: '#667781',
          cursor: 'pointer',
          padding: '4px 8px 12px',
          fontSize: 13,
        }}
      >
        <ArrowLeftOutlined />
        <span>{kindLabel}</span>
      </button>
      <div
        role="button"
        tabIndex={0}
        style={sourceRowStyle}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f2f5'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        onClick={handleLibrarySelect}
        onKeyDown={(e) => e.key === 'Enter' && handleLibrarySelect()}
      >
        <PlayCircleOutlined style={{ fontSize: 18, color: '#54656f' }} />
        <span style={{ fontSize: 14, color: '#111b21' }}>From Media</span>
      </div>
      {canPickFromDevice ? (
        <button
          type="button"
          style={{
            ...sourceRowStyle,
            margin: 0,
            width: '100%',
            border: 'none',
            background: 'transparent',
            textAlign: 'left',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f2f5'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          onClick={openDevicePicker}
        >
          <DesktopOutlined style={{ fontSize: 18, color: '#54656f' }} />
          <span style={{ fontSize: 14, color: '#111b21' }}>From Device</span>
        </button>
      ) : (
        <div style={sourceRowStyle}>
          <DesktopOutlined style={{ fontSize: 18, color: '#54656f' }} />
          <span style={{ fontSize: 14, color: '#111b21' }}>From Device</span>
        </div>
      )}
    </div>
  );

  const popoverContent = view === 'grid' ? gridContent : sourceContent;

  const trigger = variant === 'add' ? (
    <div
      style={{
        flexShrink: 0,
        width: 56,
        height: 56,
        borderRadius: 8,
        border: '1px dashed #bfbfbf',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        color: '#8c8c8c',
        background: '#fafafa',
      }}
    >
      <PlusOutlined style={{ fontSize: 22 }} />
    </div>
  ) : (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: '#1890ff',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
      }}
    >
      <PaperClipOutlined style={{ fontSize: showLabel ? '18px' : '20px' }} />
      {showLabel && <span style={{ color: '#000' }}>Attach</span>}
    </div>
  );

  const deviceFileInput = (
    <input
      ref={deviceInputRef}
      type="file"
      multiple={devicePickerKind !== 'audio'}
      accept={deviceAccept || undefined}
      onChange={handleDeviceFileChange}
      tabIndex={-1}
      aria-hidden
      style={{
        position: 'fixed',
        left: '-10000px',
        width: 1,
        height: 1,
        opacity: 0,
        pointerEvents: 'none',
      }}
    />
  );

  return (
    <>
      {typeof document !== 'undefined'
        ? createPortal(deviceFileInput, document.body)
        : null}

      <Tooltip title="Attach">
        <Popover
          placement="topLeft"
          trigger="click"
          open={isDisabled ? false : popoverOpen}
          onOpenChange={(open) => {
            if (isDisabled) {
              return;
            }
            setPopoverOpen(open);
            if (!open) {
              resetPopover();
            }
          }}
          content={popoverContent}
          overlayInnerStyle={{
            padding: 10,
            borderRadius: 12,
            boxShadow: '0 2px 12px rgba(11, 20, 26, 0.15)',
          }}
        >
          {trigger}
        </Popover>
      </Tooltip>

      <UploadDrawer
        ref={uploadRef}
        {...drawerProps}
        hideTrigger
        mediaKind={openLibraryKind || activeKind || 'image'}
        openLibraryRequest={openLibraryKind}
        onLibraryOpened={handleLibraryOpened}
      />

      <LocationSendDrawer
        open={locationOpen}
        onClose={() => setLocationOpen(false)}
        onSendLocation={isDisabled ? undefined : onSendLocation}
        isMobile={isMobile}
      />
    </>
  );
};

export default AttachmentMenu;
